import { defineStore } from 'pinia';
import { Buildings, Tokens } from '~/utils/constants';
import type { IUserModel, IUserSubscribers } from '~/types/apiService';
import type { TNullable } from '~/types/common';
import type { Districts } from '~/types/districts';
import type { CraftAvailableForClaim, RunningRecipeProps } from '~/types/crafting';
import { setCookie, getCookie } from '~/utils/cookieVolume';

export const useMainStore = defineStore('main', {
  state: () => ({
    error: '' as unknown,
    notificationUpdateVersion: 0,
    currentToken: Tokens.pearl,
    userData: null as TNullable<IUserModel>,
    userSubscribers: null as TNullable<IUserSubscribers>,
    weatherValue: 0,
    buyPackCounter: 0,
    currentDistrict: null as Districts | null,
    isNewYearTheme: false,
    isJoiningToReferral: false,
    craftingTokenImg: '',
    isShowSoundControlsModal: false,
    userBlockInfoModalOpen: false,
    openedBuilding: null as TNullable<Buildings>,
    volume: {
      music: 0.3,
      ambient: 0.1
    },
    // Unclaimed modifiers states
    unclaimedModifiers: {} as Record<number, any>,
    cachedClaimItems: [] as CraftAvailableForClaim[],
    isLoadingUnclaimedModifiers: false
  }),
  getters: {
    getError: (state) => state.error
  },
  actions: {
    updateBuypackCounter() {
      this.buyPackCounter++;
    },
    setCurrentDistrict(district: Districts | null) {
      this.currentDistrict = district;
    },
    setError(error: unknown) {
      this.error = error;
    },
    updateVersion() {
      this.notificationUpdateVersion++;
    },
    setCraftingTokenImg(img: string) {
      this.craftingTokenImg = img;
    },
    setCurrentToken(token: Tokens) {
      this.currentToken = token;
    },
    setUserData(userData: IUserModel | null) {
      this.userData = userData;
    },
    setUserSubscribersData(userSubscribers: IUserSubscribers) {
      this.userSubscribers = userSubscribers;
    },
    setRandomWeatherValue() {
      const now = new Date();
      const seed = now.getDate() + now.getMonth() + now.getHours();
      const x = Math.sin(seed) * 10000;
      this.weatherValue = Math.floor((x - Math.floor(x)) * 12) + 1;
    },
    setIsNewYearTheme(value: boolean) {
      this.isNewYearTheme = value;
    },
    setIsJoiningToReferral(value: boolean) {
      this.isJoiningToReferral = value;
    },

    // volume setting
    initVolumeFromCookieOrLocaleStorage() {
      try {
        const src = getCookie('volume') || window.localStorage.getItem('volume');

        if (src) {
          const parsed = JSON.parse(src);
          this.volume.music = parsed.music ?? this.volume.music;
          this.volume.ambient = parsed.ambient ?? this.volume.ambient;
        }
      } catch (e) {
        console.warn('[VolumeStore] Failed to load volume:', e);
      }
    },
    setMusicVolume(volume: number) {
      this.volume.music = volume;
    },
    setAmbientVolume(volume: number) {
      this.volume.ambient = volume;
    },
    saveVolumeToCookieAndLocaleStorage() {
      try {
        const serialized = JSON.stringify(this.volume);
        setCookie('volume', serialized, 3650);
        window.localStorage.setItem('volume', serialized);
      } catch (e) {
        console.warn('[VolumeStore] Failed to save volume:', e);
      }
    },

    // Unclaimed modifiers actions
    setUnclaimedModifiers(modifiers: Record<number, any>) {
      this.unclaimedModifiers = modifiers;
    },
    setCachedClaimItems(claimItems: CraftAvailableForClaim[]) {
      this.cachedClaimItems = Array.isArray(claimItems) ? (claimItems as CraftAvailableForClaim[]) : [];
      if (!Array.isArray(claimItems)) {
        console.warn('setCachedClaimItems got non-array, cache cleared:', claimItems);
      }
    },
    clearCachedClaimItems() {
      this.cachedClaimItems = [];
    },
    setIsLoadingUnclaimedModifiers(loading: boolean) {
      this.isLoadingUnclaimedModifiers = loading;
    },
    getUnclaimedModifier(runningRecipe: RunningRecipeProps) {
      const cached = Object.values(this.unclaimedModifiers).find((modifier) => {
        return (
          modifier.recipeId.toString() === runningRecipe.recipeId.toString() &&
          modifier.userRecipeIndex === runningRecipe.userRecipeIndex
        );
      });
      return cached ? cached.unclaimedModifier : 0;
    },
    getClaimItemsForBuilding(buildingHash: string) {
      return (Array.isArray(this.cachedClaimItems) ? this.cachedClaimItems : []).filter((item) =>
        Object.values(item.unclaimedByRecipeIndexes || {}).some((modifier) => modifier.buildingHash === buildingHash)
      );
    }
  },
  persist: {
    serializer: {
      serialize: (state) => {
        return JSON.stringify(state, (key, value) => {
          if (typeof value === 'bigint') {
            return Number(value);
          }
          return value;
        });
      },
      deserialize: (str) => {
        return JSON.parse(str);
      }
    }
  }
});
